import React, { useState, useEffect } from 'react';
import UserLayout from '../../components/layouts/UserLayout';
import { userAPI, ticketAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Download, Calendar, MapPin, Clock, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice, formatDateTime, formatSlotDate, formatSlotTime } from '../../lib/utils';

const MemberDownloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDownloads();
  }, []);

  const loadDownloads = async () => {
    try {
      const response = await userAPI.getDownloads();
      setDownloads(response.data);
    } catch (error) {
      toast.error('Failed to load downloads');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (download) => {
    if (!download.download_token) {
      toast.error('Download link expired. Please contact support.');
      return;
    }
    
    try {
      // Use fetch to handle the download properly and catch errors
      const url = ticketAPI.getFile(download.download_token);
      const token = localStorage.getItem('token');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        // Try to get error message from response
        try {
          const errorData = await response.json();
          const errorMsg = errorData.detail || 'Failed to download file';
          // Show more helpful message if file is not available
          if (errorMsg.includes('not available') || errorMsg.includes('not found')) {
            toast.error('This ticket file is no longer available. Please contact admin for a new ticket.');
          } else {
            toast.error(errorMsg);
          }
        } catch {
          toast.error('Failed to download file. The file may no longer be available.');
        }
        return;
      }
      
      // Check if response is actually a file (not JSON error)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to download file');
        return;
      }
      
      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `ticket_${download.monument_name || 'download'}_${download.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Ticket downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download ticket. Please try again later.');
    }
  };

  return (
    <UserLayout title="Download History" subtitle="All your downloaded tickets">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Your Downloads
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : downloads.length === 0 ? (
            <div className="text-center py-12">
              <Download className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No downloads yet</h3>
              <p className="text-slate-500 mt-1">Your downloaded tickets will appear here</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Monument</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Access Date</TableHead>
                  <TableHead>Access Time</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Downloaded</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {downloads.map((download) => (
                  <TableRow key={download.id} data-testid={`download-row-${download.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{download.monument_name || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{download.category_label || 'N/A'}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {formatSlotDate(download.slot_start_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {formatSlotTime(download.slot_start_at)}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatPrice(download.price_eur_cents)}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDateTime(download.downloaded_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadFile(download)}
                        disabled={!download.download_token}
                        data-testid={`download-btn-${download.id}`}
                        className="flex items-center gap-1"
                      >
                        <FileDown className="w-4 h-4" />
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default MemberDownloads;
